// frontend/src/ComprehensiveAuditReport.tsx - Comprehensive Audit Report with Export Capabilities

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface ComprehensiveAuditReportProps {
  processId: number;
  processName: string;
  userRole: 'ADMIN' | 'PROCESS_OWNER' | 'EVALUATION_PANEL_MEMBER' | 'AUDITOR' | 'SUPER_ADMIN';
  onBack: () => void;
}

interface EvaluationDetail {
  evaluator_id: number;
  evaluator_name: string;
  total_score: number;
  mandatory_passed: number | null;
  is_complete: boolean;
  completed_at: string | null;
  vfm_score: number | null;
}

interface DetailedSupplierReport {
  supplier_id: number;
  supplier_name: string;
  abn_acn: string;
  total_offered_price: number | null;
  compliance_status: string;
  compliance_details: any[];
  evaluations: EvaluationDetail[];
  criterion_scores: any;
  overall_average: number | null;
  ranking: number;
  vfm_score: number | null;
  recommendation: string;
}

interface PriceQualityAnalysis {
  baseline_supplier: {
    supplier_id: number;
    supplier_name: string;
    cost: number;
    quality: number;
  } | null;
  quality_premiums: QualityPremiumAnalysis[];
  analysis_available: boolean;
  total_suppliers_analysed?: number;
  message?: string;
}

interface QualityPremiumAnalysis {
  supplier_id: number;
  supplier_name: string;
  cost_difference: number;
  quality_difference: number;
  quality_premium: number | null;
  premium_category: 'baseline' | 'excellent' | 'good' | 'expensive' | 'very_expensive' | 'poor_value' | 'exceptional';
  recommendation: string;
  is_baseline: boolean;
}

interface ComplianceDetail {
  criterion: string;
  status: string;
  comments: string | null;
  checked_at: string | null;
}

interface SupplierComplianceDetail {
  supplier_id: number;
  supplier_name: string;
  status: string;
  details: ComplianceDetail[];
}

interface ComplianceOverview {
  total_suppliers: number;
  compliant_suppliers: number;
  non_compliant_suppliers: number;
  not_checked_suppliers: number;
  compliance_rate: number;
  criteria_details: any[];
  supplier_compliance_details: SupplierComplianceDetail[];
}

interface SupplierRanking {
  ranking: number;
  supplier_name: string;
  abn_acn: string;
  average_score: number | null;
  total_offered_price: number | null;
  vfm_score: number | null;
  compliance_status: string;
  completed_evaluations: number;
  evaluator_count: number;
}

interface EvaluatorSummary {
  evaluator_name: string;
  evaluator_email: string;
  total_assignments: number;
  completed_evaluations: number;
  in_progress_evaluations: number;
  completion_rate: number;
  average_score_given: number | null;
  last_activity: string | null;
}

interface SupplierResult {
  supplier_id: number;
  supplier_name: string;
  status: string;
  comments: string | null;
}

interface MandatoryAnalysis {
  requirement_id: number;
  requirement_description: string;
  total_evaluations: number;
  pass_count: number;
  fail_count: number;
  pass_rate: number;
  supplier_results: SupplierResult[];
}

interface AuditReportData {
  processSummary: any;
  supplierRankings: SupplierRanking[];
  evaluatorSummaries: EvaluatorSummary[];
  criterionAnalysis: any[];
  complianceOverview: ComplianceOverview;
  mandatoryAnalysis: MandatoryAnalysis[];
  detailedSupplierReports: DetailedSupplierReport[];
  auditTrail: any[];
}

const ComprehensiveAuditReport: React.FC<ComprehensiveAuditReportProps> = ({
  processId,
  processName,
  userRole,
  onBack
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx' | 'pdf'>('pdf');
  const [reportData, setReportData] = useState<AuditReportData | null>(null);
  const [priceQualityAnalysis, setPriceQualityAnalysis] = useState<PriceQualityAnalysis | null>(null);
  const [processInfo, setProcessInfo] = useState<{evaluation_methodology: string, score_scale: string} | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const loadCompleteAuditData = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

      // Load process information to get evaluation methodology
      let methodology = 'vfm';
      try {
        const processResponse = await axios.get(`${API_URL}/api/processes/${processId}/complete-data`, authHeaders);
        methodology = processResponse.data.process.evaluation_methodology || 'vfm';
        setProcessInfo({
          evaluation_methodology: methodology,
          score_scale: processResponse.data.process.score_scale || '0-5'
        });
      } catch (error) {
        console.warn('Process information not available:', error);
        setProcessInfo({ evaluation_methodology: 'vfm', score_scale: '0-5' });
      }

      // Load Price/Quality Analysis only if methodology is price_quality
      if (methodology === 'price_quality') {
        try {
          const priceQualityResponse = await axios.get(`${API_URL}/api/reports/process/${processId}/price-quality-analysis`, authHeaders);
          setPriceQualityAnalysis(priceQualityResponse.data);
        } catch (error) {
          console.warn('Price/Quality analysis not available:', error);
          setPriceQualityAnalysis(null);
        }
      } else {
        setPriceQualityAnalysis(null);
      }

      // Load all report data in parallel for comprehensive audit
      const [
        processSummaryRes,
        supplierRankingsRes,
        evaluatorSummariesRes,
        criterionAnalysisRes,
        complianceOverviewRes,
        mandatoryAnalysisRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/reports/process/${processId}/summary`, authHeaders),
        axios.get(`${API_URL}/api/reports/process/${processId}/supplier-rankings`, authHeaders),
        axios.get(`${API_URL}/api/reports/process/${processId}/evaluator-summary`, authHeaders),
        axios.get(`${API_URL}/api/reports/process/${processId}/criterion-analysis`, authHeaders),
        axios.get(`${API_URL}/api/reports/process/${processId}/compliance-overview`, authHeaders),
        axios.get(`${API_URL}/api/reports/process/${processId}/mandatory-analysis`, authHeaders)
      ]);

      // Load detailed supplier reports for each supplier
      const detailedSupplierReports = [];
      for (const supplier of supplierRankingsRes.data) {
        try {
          const detailedRes = await axios.get(
            `${API_URL}/api/reports/process/${processId}/detailed-supplier/${supplier.supplier_id}`,
            authHeaders
          );
          detailedSupplierReports.push(detailedRes.data);
        } catch (error) {
          console.warn(`Could not load detailed report for supplier ${supplier.supplier_id}`);
        }
      }

      setReportData({
        processSummary: processSummaryRes.data,
        supplierRankings: supplierRankingsRes.data,
        evaluatorSummaries: evaluatorSummariesRes.data,
        criterionAnalysis: criterionAnalysisRes.data,
        complianceOverview: complianceOverviewRes.data,
        mandatoryAnalysis: mandatoryAnalysisRes.data,
        detailedSupplierReports,
        auditTrail: [] // Will be populated with audit trail data
      });

    } catch (error) {
      console.error('Error loading audit data:', error);
      showNotification('error', 'Failed to load audit data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompleteAuditData();
  }, [processId]);

  const exportToCSV = () => {
    if (!reportData) return;

    const csvSections = [];

    // Process Summary Section
    csvSections.push('PROCESS SUMMARY');
    csvSections.push(`Process Name,${reportData.processSummary.process_name}`);
    csvSections.push(`ITO Reference,${reportData.processSummary.ito_reference}`);
    csvSections.push(`Status,${reportData.processSummary.status}`);
    csvSections.push(`Total Suppliers,${reportData.processSummary.total_suppliers}`);
    csvSections.push(`Compliant Suppliers,${reportData.processSummary.compliant_suppliers}`);
    csvSections.push(`Completion Percentage,${reportData.processSummary.completion_percentage}%`);
    csvSections.push(`Average Score,${reportData.processSummary.average_process_score || 'N/A'}`);
    csvSections.push('');

    // Supplier Rankings Section
    csvSections.push('SUPPLIER RANKINGS');
    csvSections.push('Rank,Supplier Name,ABN/ACN,Average Score,Price,VFM Score,Compliance Status,Completed Evaluations');
    reportData.supplierRankings.forEach((supplier: SupplierRanking) => {
      csvSections.push(
        `${supplier.ranking},${supplier.supplier_name},${supplier.abn_acn},${supplier.average_score || 'N/A'},${supplier.total_offered_price || 'N/A'},${supplier.vfm_score || 'N/A'},${supplier.compliance_status},${supplier.completed_evaluations}/${supplier.evaluator_count}`
      );
    });
    csvSections.push('');

    // Evaluator Performance Section
    csvSections.push('EVALUATION PANEL MEMBER PERFORMANCE');
    csvSections.push('Evaluator Name,Email,Total Assignments,Completed,In Progress,Completion Rate,Average Score Given,Last Activity');
    reportData.evaluatorSummaries.forEach((evaluator: EvaluatorSummary) => {
      csvSections.push(
        `${evaluator.evaluator_name},${evaluator.evaluator_email},${evaluator.total_assignments},${evaluator.completed_evaluations},${evaluator.in_progress_evaluations},${evaluator.completion_rate}%,${evaluator.average_score_given || 'N/A'},${evaluator.last_activity || 'N/A'}`
      );
    });
    csvSections.push('');

    // Compliance Overview Section
    csvSections.push('COMPLIANCE OVERVIEW');
    csvSections.push(`Total Suppliers,${reportData.complianceOverview.total_suppliers}`);
    csvSections.push(`Compliant,${reportData.complianceOverview.compliant_suppliers}`);
    csvSections.push(`Non-Compliant,${reportData.complianceOverview.non_compliant_suppliers}`);
    csvSections.push(`Not Checked,${reportData.complianceOverview.not_checked_suppliers}`);
    csvSections.push(`Compliance Rate,${reportData.complianceOverview.compliance_rate}%`);
    csvSections.push('');

    // Detailed Individual Evaluations Section
    csvSections.push('DETAILED INDIVIDUAL EVALUATIONS');
    csvSections.push('Supplier Name,Evaluator,Total Score,Mandatory Passed,Completion Status,VFM Score');
    reportData.detailedSupplierReports.forEach((supplier: DetailedSupplierReport) => {
      supplier.evaluations.forEach((evaluation: EvaluationDetail) => {
        csvSections.push(
          `${supplier.supplier_name},${evaluation.evaluator_name},${evaluation.total_score},${evaluation.mandatory_passed || 'N/A'},${evaluation.is_complete ? 'Complete' : 'In Progress'},${evaluation.vfm_score || 'N/A'}`
        );
      });
    });

    const csvContent = csvSections.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${processName}_Comprehensive_Audit_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showNotification('success', 'CSV report exported successfully');
  };

  const exportToXLSX = () => {
    // For XLSX export, we would typically use a library like xlsx-js
    // For now, we'll create a structured HTML table that Excel can import
    if (!reportData) return;

    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Comprehensive Audit Report - ${processName}</title>
          <style>
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .section-header { background-color: #4472C4; color: white; font-weight: bold; }
            .summary-section { background-color: #D5E4F7; }
          </style>
        </head>
        <body>
          <h1>Comprehensive Audit Report</h1>
          <h2>Process: ${processName}</h2>
          <p>Generated: ${new Date().toLocaleString()}</p>
          
          <h3>Process Summary</h3>
          <table>
            <tr><th>Metric</th><th>Value</th></tr>
            <tr><td>ITO Reference</td><td>${reportData.processSummary.ito_reference}</td></tr>
            <tr><td>Status</td><td>${reportData.processSummary.status}</td></tr>
            <tr><td>Total Suppliers</td><td>${reportData.processSummary.total_suppliers}</td></tr>
            <tr><td>Compliant Suppliers</td><td>${reportData.processSummary.compliant_suppliers}</td></tr>
            <tr><td>Completion Percentage</td><td>${reportData.processSummary.completion_percentage}%</td></tr>
            <tr><td>Average Score</td><td>${reportData.processSummary.average_process_score || 'N/A'}</td></tr>
          </table>

          <h3>Supplier Rankings</h3>
          <table>
            <tr>
              <th>Rank</th><th>Supplier Name</th><th>ABN/ACN</th><th>Average Score</th>
              <th>Price (AUD)</th><th>VFM Score</th><th>Compliance</th><th>Evaluations</th>
            </tr>
            ${reportData.supplierRankings.map((supplier: SupplierRanking) => `
              <tr>
                <td>${supplier.ranking}</td>
                <td>${supplier.supplier_name}</td>
                <td>${supplier.abn_acn}</td>
                <td>${supplier.average_score || 'N/A'}</td>
                <td>${supplier.total_offered_price ? '$' + supplier.total_offered_price.toLocaleString() : 'N/A'}</td>
                <td>${supplier.vfm_score || 'N/A'}</td>
                <td>${supplier.compliance_status}</td>
                <td>${supplier.completed_evaluations}/${supplier.evaluator_count}</td>
              </tr>
            `).join('')}
          </table>

          <h3>Individual Evaluation Scores</h3>
          <table>
            <tr>
              <th>Supplier</th><th>Evaluator</th><th>Total Score</th>
              <th>Mandatory Passed</th><th>Status</th><th>VFM Score</th>
            </tr>
            ${reportData.detailedSupplierReports.map((supplier: DetailedSupplierReport) => 
              supplier.evaluations.map((evaluation: EvaluationDetail) => `
                <tr>
                  <td>${supplier.supplier_name}</td>
                  <td>${evaluation.evaluator_name}</td>
                  <td>${evaluation.total_score}</td>
                  <td>${evaluation.mandatory_passed || 'N/A'}</td>
                  <td>${evaluation.is_complete ? 'Complete' : 'In Progress'}</td>
                  <td>${evaluation.vfm_score || 'N/A'}</td>
                </tr>
              `).join('')
            ).join('')}
          </table>

          <h3>Compliance Details</h3>
          <table>
            <tr><th>Supplier</th><th>Compliance Status</th><th>Details</th></tr>
            ${reportData.complianceOverview.supplier_compliance_details.map((supplier: SupplierComplianceDetail) => `
              <tr>
                <td>${supplier.supplier_name}</td>
                <td>${supplier.status}</td>
                <td>${supplier.details.map((d: ComplianceDetail) => `${d.criterion}: ${d.status}`).join('; ')}</td>
              </tr>
            `).join('')}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${processName}_Comprehensive_Audit_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showNotification('success', 'XLSX report exported successfully');
  };

  const exportToPDF = () => {
    if (!reportData) return;

    // Create a printable version that can be saved as PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Comprehensive Audit Report - ${processName}</title>
          <style>
            @media print {
              body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; margin: 20px; }
              .page-break { page-break-before: always; }
              .no-print { display: none; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              th, td { border: 1px solid #333; padding: 8px; text-align: left; font-size: 11px; }
              th { background-color: #f0f0f0; font-weight: bold; }
              .section-header { background-color: #4472C4; color: white; padding: 10px; margin: 20px 0 10px 0; }
              .summary-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
              .metric-card { border: 1px solid #ddd; padding: 10px; }
              .executive-summary { background-color: #f8f9fa; padding: 15px; margin: 20px 0; border-left: 4px solid #4472C4; }
              .audit-certification { background-color: #e8f4f8; padding: 15px; margin: 20px 0; border: 2px solid #4472C4; }
            }
            body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #4472C4; padding-bottom: 20px; }
            .section-header { background-color: #4472C4; color: white; padding: 10px; margin: 20px 0 10px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #333; padding: 8px; text-align: left; font-size: 11px; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .summary-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .metric-card { border: 1px solid #ddd; padding: 10px; }
            .executive-summary { background-color: #f8f9fa; padding: 15px; margin: 20px 0; border-left: 4px solid #4472C4; }
            .audit-certification { background-color: #e8f4f8; padding: 15px; margin: 20px 0; border: 2px solid #4472C4; }
            .print-btn { margin: 20px 0; padding: 10px 20px; background: #4472C4; color: white; border: none; cursor: pointer; }
          </style>
        </head>
        <body>
          <button class="print-btn no-print" onclick="window.print()">🖨️ Print/Save as PDF</button>
          
          <div class="header">
            <h1>COMPREHENSIVE PROCUREMENT EVALUATION</h1>
            <h2>AUDIT REPORT</h2>
            <h3>Process: ${processName}</h3>
            <p><strong>ITO Reference:</strong> ${reportData.processSummary.ito_reference}</p>
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString('en-AU')}</p>
            <p><strong>Report Period:</strong> ${new Date(reportData.processSummary.created_at).toLocaleDateString('en-AU')} - ${new Date().toLocaleDateString('en-AU')}</p>
          </div>

          <div class="executive-summary">
            <h3>🎯 EXECUTIVE SUMMARY</h3>
            <p><strong>Process Status:</strong> ${reportData.processSummary.status}</p>
            <p><strong>Completion:</strong> ${reportData.processSummary.completion_percentage}% (${reportData.processSummary.completed_evaluations}/${reportData.processSummary.total_evaluations_possible} evaluations)</p>
            <p><strong>Supplier Participation:</strong> ${reportData.processSummary.total_suppliers} suppliers, ${reportData.processSummary.compliant_suppliers} compliant (${((reportData.processSummary.compliant_suppliers / reportData.processSummary.total_suppliers) * 100).toFixed(1)}%)</p>
            <p><strong>Average Evaluation Score:</strong> ${reportData.processSummary.average_process_score ? reportData.processSummary.average_process_score.toFixed(2) + '/100' : 'N/A'}</p>
            <p><strong>Process Integrity:</strong> All evaluations conducted independently with full audit trail maintained.</p>
          </div>

          <div class="section-header"><h2>📊 KEY PERFORMANCE METRICS</h2></div>
          <div class="summary-metrics">
            <div class="metric-card">
              <h4>Evaluation Completion</h4>
              <p><strong>${reportData.processSummary.completion_percentage}%</strong> Complete</p>
              <p>${reportData.processSummary.completed_evaluations} of ${reportData.processSummary.total_evaluations_possible} evaluations</p>
            </div>
            <div class="metric-card">
              <h4>Compliance Rate</h4>
              <p><strong>${reportData.complianceOverview.compliance_rate.toFixed(1)}%</strong> Compliant</p>
              <p>${reportData.complianceOverview.compliant_suppliers} of ${reportData.complianceOverview.total_suppliers} suppliers</p>
            </div>
          </div>

          <div class="page-break"></div>
          <div class="section-header"><h2>🏆 SUPPLIER EVALUATION RESULTS</h2></div>
          <table>
            <thead>
              <tr>
                <th>Rank</th><th>Supplier Name</th><th>ABN/ACN</th><th>Avg Score</th>
                <th>Price (AUD)</th><th>VFM Score</th><th>Compliance</th><th>Evaluations</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.supplierRankings.map((supplier: SupplierRanking) => `
                <tr>
                  <td><strong>${supplier.ranking}</strong></td>
                  <td><strong>${supplier.supplier_name}</strong></td>
                  <td>${supplier.abn_acn}</td>
                  <td><strong>${supplier.average_score ? supplier.average_score.toFixed(2) : 'N/A'}</strong></td>
                  <td>${supplier.total_offered_price ? '$' + supplier.total_offered_price.toLocaleString('en-AU') : 'N/A'}</td>
                  <td>${supplier.vfm_score ? supplier.vfm_score.toFixed(2) : 'N/A'}</td>
                  <td><strong>${supplier.compliance_status}</strong></td>
                  <td>${supplier.completed_evaluations}/${supplier.evaluator_count}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="page-break"></div>
          <div class="section-header"><h2>👥 EVALUATION PANEL MEMBER PERFORMANCE AUDIT</h2></div>
          <table>
            <thead>
              <tr>
                <th>Evaluator Name</th><th>Email</th><th>Assigned</th><th>Completed</th>
                <th>Completion Rate</th><th>Avg Score Given</th><th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.evaluatorSummaries.map((evaluator: EvaluatorSummary) => `
                <tr>
                  <td><strong>${evaluator.evaluator_name}</strong></td>
                  <td>${evaluator.evaluator_email}</td>
                  <td>${evaluator.total_assignments}</td>
                  <td>${evaluator.completed_evaluations}</td>
                  <td><strong>${evaluator.completion_rate}%</strong></td>
                  <td>${evaluator.average_score_given ? evaluator.average_score_given.toFixed(2) : 'N/A'}</td>
                  <td>${evaluator.last_activity ? new Date(evaluator.last_activity).toLocaleDateString('en-AU') : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="page-break"></div>
          <div class="section-header"><h2>📋 DETAILED INDIVIDUAL EVALUATIONS</h2></div>
          <table>
            <thead>
              <tr>
                <th>Supplier</th><th>Evaluator</th><th>Total Score</th><th>Mandatory Passed</th>
                <th>Status</th><th>VFM Score</th><th>Completion Date</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.detailedSupplierReports.map((supplier: DetailedSupplierReport) => 
                supplier.evaluations.map((evaluation: EvaluationDetail) => `
                  <tr>
                    <td><strong>${supplier.supplier_name}</strong></td>
                    <td>${evaluation.evaluator_name}</td>
                    <td><strong>${evaluation.total_score}</strong></td>
                    <td>${evaluation.mandatory_passed !== null ? evaluation.mandatory_passed : 'N/A'}</td>
                    <td>${evaluation.is_complete ? '✅ Complete' : '⏳ In Progress'}</td>
                    <td>${evaluation.vfm_score ? evaluation.vfm_score.toFixed(2) : 'N/A'}</td>
                    <td>${evaluation.completed_at ? new Date(evaluation.completed_at).toLocaleDateString('en-AU') : 'N/A'}</td>
                  </tr>
                `).join('')
              ).join('')}
            </tbody>
          </table>

          <div class="page-break"></div>
          <div class="section-header"><h2>✅ COMPLIANCE VERIFICATION</h2></div>
          <table>
            <thead>
              <tr><th>Supplier Name</th><th>Compliance Status</th><th>Compliance Details</th></tr>
            </thead>
            <tbody>
              ${reportData.complianceOverview.supplier_compliance_details.map((supplier: SupplierComplianceDetail) => `
                <tr>
                  <td><strong>${supplier.supplier_name}</strong></td>
                  <td><strong>${supplier.status}</strong></td>
                  <td>${supplier.details.map((d: ComplianceDetail) => `${d.criterion}: ${d.status}`).join('; ')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          ${reportData.mandatoryAnalysis.length > 0 ? `
            <div class="page-break"></div>
            <div class="section-header"><h2>⚠️ MANDATORY REQUIREMENTS ANALYSIS</h2></div>
            ${reportData.mandatoryAnalysis.map((requirement: MandatoryAnalysis) => `
              <h4>${requirement.requirement_description}</h4>
              <p><strong>Pass Rate:</strong> ${requirement.pass_rate.toFixed(1)}% (${requirement.pass_count}/${requirement.total_evaluations})</p>
              <table>
                <thead>
                  <tr><th>Supplier</th><th>Status</th><th>Comments</th></tr>
                </thead>
                <tbody>
                  ${requirement.supplier_results.map((result: SupplierResult) => `
                    <tr>
                      <td>${result.supplier_name}</td>
                      <td><strong>${result.status}</strong></td>
                      <td>${result.comments || 'No comments'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `).join('')}
          ` : ''}

          <div class="page-break"></div>
          <div class="audit-certification">
            <h3>🛡️ AUDIT CERTIFICATION</h3>
            <p><strong>Process Integrity:</strong> This procurement evaluation process has been conducted in accordance with established governance procedures, maintaining full transparency and accountability throughout.</p>
            <p><strong>Data Integrity:</strong> All evaluation scores, compliance checks, and supplier information have been verified and validated through the EvaluTool system with complete audit trail.</p>
            <p><strong>Evaluation Independence:</strong> All evaluators completed their assessments independently without access to other evaluator scores or pricing information.</p>
            <p><strong>Compliance Verification:</strong> All suppliers have been assessed against mandatory compliance criteria with results documented and verifiable.</p>
            <p><strong>Report Date:</strong> ${new Date().toLocaleDateString('en-AU')}</p>
            <p><strong>System Version:</strong> EvaluTool ${reportData.processSummary.process_id}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.focus();

    showNotification('success', 'PDF report opened in new window - use browser Print function to save as PDF');
  };

  const handleExport = () => {
    switch (exportFormat) {
      case 'csv':
        exportToCSV();
        break;
      case 'xlsx':
        exportToXLSX();
        break;
      case 'pdf':
        exportToPDF();
        break;
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      marginBottom: 'var(--spacing-xl)',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-xs)'
    }
  };

  return (
    <div style={styles.container}>
      {notification && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}>
          {notification.message}
        </div>
      )}

      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ margin: 0, fontSize: '2.5rem', color: '#1e293b', marginBottom: '0.5rem' }}>
            📋 Comprehensive Audit Report
          </h1>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#6b7280', marginBottom: '1rem' }}>
            {processName}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
            Complete evaluation audit with individual scoring details and compliance verification
          </p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '1.2rem', color: '#374151' }}>
              Loading comprehensive audit data...
            </div>
          </div>
        ) : reportData ? (
          <>
            {/* Summary Overview */}
            <div style={{ 
              background: '#f8fafc', 
              padding: '2rem', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              border: '2px solid #e2e8f0'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.3rem' }}>
                🎯 Audit Summary Overview
              </h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem' 
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>
                    {reportData.processSummary.completion_percentage}%
                  </div>
                  <div style={{ color: '#6b7280' }}>Evaluation Complete</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>
                    {reportData.supplierRankings.length}
                  </div>
                  <div style={{ color: '#6b7280' }}>Suppliers Evaluated</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>
                    {reportData.detailedSupplierReports.reduce((total, supplier) => 
                      total + supplier.evaluations.length, 0
                    )}
                  </div>
                  <div style={{ color: '#6b7280' }}>Individual Evaluations</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7c3aed' }}>
                    {reportData.complianceOverview.compliance_rate.toFixed(1)}%
                  </div>
                  <div style={{ color: '#6b7280' }}>Compliance Rate</div>
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div style={{ 
              background: '#eff6ff', 
              padding: '2rem', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              border: '2px solid #bfdbfe'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#1e40af', fontSize: '1.3rem' }}>
                📥 Export Comprehensive Audit Report
              </h3>
              <p style={{ color: '#1e40af', marginBottom: '1.5rem' }}>
                Generate a complete audit report including all individual scores, compliance details, 
                evaluator performance, and audit trail information.
              </p>
              
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ marginRight: '0.5rem', fontWeight: 'bold', color: '#374151' }}>
                    Export Format:
                  </label>
                  <select 
                    value={exportFormat} 
                    onChange={(e) => setExportFormat(e.target.value as 'csv' | 'xlsx' | 'pdf')}
                    style={{ 
                      padding: '0.5rem', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="csv">📊 CSV (Excel Compatible)</option>
                    <option value="xlsx">📈 XLSX (Excel Workbook)</option>
                    <option value="pdf">📄 PDF (Audit Document)</option>
                  </select>
                </div>
                
                <button 
                  onClick={handleExport}
                  style={{
                    ...styles.button,
                    background: 'linear-gradient(45deg, #2563eb, #7c3aed)',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem'
                  }}
                >
                  📥 Generate {exportFormat.toUpperCase()} Report
                </button>
              </div>

              <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                <h4 style={{ margin: '0.5rem 0', color: '#374151' }}>Report Contents Include:</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Complete process summary and metrics</li>
                  <li>Individual evaluation scores for each supplier by each evaluator</li>
                  <li>Detailed compliance verification results</li>
                  <li>Evaluator performance and audit trail</li>
                  <li>Value for Money (VFM) analysis</li>
                  {processInfo?.evaluation_methodology === 'price_quality' && (
                    <li>Price/Quality strategic methodology analysis</li>
                  )}
                  {processInfo?.score_scale === 'requirements_based' && (
                    <li>Requirements-Based Normalisation scoring analysis</li>
                  )}
                  <li>Mandatory requirements assessment</li>
                  <li>Process integrity certification</li>
                </ul>
              </div>
            </div>

            {/* Price/Quality Strategic Analysis */}
            {priceQualityAnalysis && priceQualityAnalysis.analysis_available && (
              <div style={{ ...styles.card, marginTop: '1.5rem' }}>
                <h3 style={{ 
                  color: '#2563eb', 
                  margin: '0 0 1rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💰 Price/Quality Strategic Analysis
                </h3>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>
                    📍 Baseline Supplier: {priceQualityAnalysis.baseline_supplier?.supplier_name}
                  </h4>
                  <div style={{ 
                    background: '#f0f9ff', 
                    padding: '0.75rem', 
                    borderRadius: '8px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#1e40af' }}>
                      <strong>Lowest Cost:</strong> ${priceQualityAnalysis.baseline_supplier?.cost?.toLocaleString() || 'N/A'} • 
                      <strong> Quality Score:</strong> {priceQualityAnalysis.baseline_supplier?.quality?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 style={{ color: '#374151', marginBottom: '1rem' }}>🎯 Quality Premium Analysis</h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {priceQualityAnalysis.quality_premiums.map((analysis) => {
                      const getCategoryColor = (category: string) => {
                        switch (category) {
                          case 'baseline': return { bg: '#f0f9ff', border: '#2563eb', icon: '📍' };
                          case 'excellent': return { bg: '#f0fdf4', border: '#10b981', icon: '🟢' };
                          case 'good': return { bg: '#fefce8', border: '#eab308', icon: '🟡' };
                          case 'expensive': return { bg: '#fef3c7', border: '#f59e0b', icon: '🟠' };
                          case 'very_expensive': return { bg: '#fef2f2', border: '#ef4444', icon: '🔴' };
                          case 'exceptional': return { bg: '#ecfdf5', border: '#059669', icon: '⭐' };
                          case 'poor_value': return { bg: '#f3f4f6', border: '#6b7280', icon: '❌' };
                          default: return { bg: '#f9fafb', border: '#d1d5db', icon: '?' };
                        }
                      };
                      
                      const colors = getCategoryColor(analysis.premium_category);
                      
                      return (
                        <div 
                          key={analysis.supplier_id}
                          style={{
                            background: colors.bg,
                            padding: '1rem',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <span style={{ fontSize: '1.1rem' }}>{colors.icon}</span>
                              <h5 style={{ margin: 0, color: '#1f2937', fontWeight: 'bold' }}>
                                {analysis.supplier_name}
                              </h5>
                            </div>
                            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#4b5563' }}>
                              {analysis.recommendation}
                            </p>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                              <span>Cost Difference: ${analysis.cost_difference.toLocaleString()}</span>
                              <span style={{ margin: '0 0.5rem' }}>•</span>
                              <span>Quality Difference: {analysis.quality_difference.toFixed(2)} pts</span>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: '120px' }}>
                            {analysis.quality_premium !== null ? (
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#1f2937' }}>
                                  ${analysis.quality_premium.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                  per quality point
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontWeight: 'bold', color: colors.border }}>
                                {analysis.is_baseline ? 'BASELINE' : 'N/A'}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  background: '#f8fafc', 
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  color: '#475569'
                }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>📊 Analysis Guide:</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                    <span><strong>🟢 Excellent:</strong> &lt; $3,000/point</span>
                    <span><strong>🟡 Good:</strong> $3,000 - $7,000/point</span>
                    <span><strong>🟠 Expensive:</strong> $7,000 - $12,000/point</span>
                    <span><strong>🔴 Very Expensive:</strong> &gt; $12,000/point</span>
                  </div>
                </div>
              </div>
            )}

            {priceQualityAnalysis && !priceQualityAnalysis.analysis_available && (
              <div style={{ ...styles.card, marginTop: '1.5rem' }}>
                <h3 style={{ color: '#6b7280', margin: '0 0 0.5rem 0' }}>
                  💰 Price/Quality Analysis
                </h3>
                <p style={{ margin: 0, color: '#9ca3af', fontStyle: 'italic' }}>
                  {priceQualityAnalysis.message || 'Price/Quality analysis not available for this process.'}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderTop: '2px solid #f3f4f6',
              paddingTop: '1.5rem'
            }}>
              <button 
                onClick={onBack}
                style={{
                  ...styles.button,
                  background: 'var(--color-surface-variant)',
                  color: 'var(--color-text-primary)',
                  border: '2px solid var(--color-primary)'
                }}
              >
                ← Back to Dashboard
              </button>
              
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ComprehensiveAuditReport;